import { API_BASE_URL } from "@/config";
import { MAIN_CONTROLLER } from "@/utils/main_controller.js";

import BreadCrumb from "@/components/BreadCrumb/BreadCrumb.vue";
import ClientLogo from "@/components/ClientLogo/ClientLogo.vue";
import PersonalDetails from "@/layouts/credit_bureau_services/seriti_forms/personal_details/PersonalDetails.vue";
import MaritalInformation from "@/layouts/credit_bureau_services/seriti_forms/marital_information/MaritalInformation.vue";
import AddressDetails from "@/layouts/credit_bureau_services/seriti_forms/address_details/AddressDetails.vue";
import EmploymentDetails from "@/layouts/credit_bureau_services/seriti_forms/employment_details/EmploymentDetails.vue";
import SalaryDetails from "@/layouts/credit_bureau_services/seriti_forms/salary_details/SalaryDetails.vue";
import QualifyingCriteria from "@/layouts/credit_bureau_services/seriti_forms/qualifying_criteria/QualifyingCriteria.vue";
import SupportDocumentation from "@/layouts/credit_bureau_services/seriti_forms/support_documents/SupportDocumentation.vue";

export default {
  name: "SeritiQuestions",
  mixins: [MAIN_CONTROLLER],
  components: {
    ClientLogo,
    BreadCrumb,
    PersonalDetails,
    MaritalInformation,
    AddressDetails,
    EmploymentDetails,
    SalaryDetails,
    QualifyingCriteria,
    SupportDocumentation,
  },
  data() {
    return {
      steps: [
        {
          title: "Personal Details",
          component: "PersonalDetails",
          is_valid: false,
        },
        {
          title: "Marital Information",
          component: "MaritalInformation",
          is_valid: false,
        },
        {
          title: "Address Details",
          component: "AddressDetails",
          is_valid: false,
        },
        {
          title: "Employment Details",
          component: "EmploymentDetails",
          is_valid: false,
        },
        {
          title: "Salary Details",
          component: "SalaryDetails",
          is_valid: false,
        },
        {
          title: "Qualifying Criteria",
          component: "QualifyingCriteria",
          is_valid: false,
        },
        {
          title: "Support Documentation",
          component: "SupportDocumentation",
          is_valid: false,
        },
      ],
      current_step: Number(localStorage.getItem("seriti_current_step") || 0),
      data_ready: true, // Set to true after loading any required data
      all_sections_complete: false,
      is_submitting: false,
      employment_type: "",
    };
  },
  computed: {
    should_skip_salary_details() {
      return (
        this.employment_type === "UNEMPLOYED" ||
        this.employment_type === "RETIRED"
      );
    },
    salary_details_step_index() {
      return this.steps.findIndex((step) => step.component === "SalaryDetails");
    },
  },
  methods: {
    isStepDisabled(stepIndex) {
      return (
        stepIndex === this.salary_details_step_index &&
        this.should_skip_salary_details
      );
    },
    prev_step() {
      if (this.current_step > 0) {
        this.steps[this.current_step].is_valid = false;
        let new_step = this.current_step - 1;

        if (
          this.should_skip_salary_details &&
          new_step === this.salary_details_step_index
        ) {
          new_step--;
        }

        this.current_step = Math.max(0, new_step);
        localStorage.setItem("seriti_current_step", this.current_step);
      }
    },
    on_validity_change(is_valid, payload = null) {
      this.steps[this.current_step].is_valid = is_valid;

      if (
        payload &&
        payload.employment_type &&
        this.steps[this.current_step].component === "EmploymentDetails"
      ) {
        this.employment_type = payload.employment_type;
        localStorage.setItem("seriti_employment_type", this.employment_type);
      }

      if (is_valid) {
        if (this.current_step === this.steps.length - 1) {
          this.submit_application();
        } else {
          let new_step = this.current_step + 1;

          if (
            this.should_skip_salary_details &&
            new_step === this.salary_details_step_index
          ) {
            this.steps[this.salary_details_step_index].is_valid = false;
            new_step++;
          }

          this.current_step = new_step;
          localStorage.setItem("seriti_current_step", this.current_step);
        }
      }
    },
    async get_saved_section_responses(section_name) {
      const TOKEN = localStorage.getItem("token");
      const API_URL = `${API_BASE_URL}/verify_api/financial_information_api/get_saved_section_responses/`;
      const workflow_id = this.$store.getters.get_workflow_id;

      try {
        const response = await $.ajax({
          url: API_URL,
          method: "GET",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          data: {
            section_name,
            workflow_id,
          },
        });
        return response.responses || [];
      } catch (error) {
        return [];
      }
    },
    async check_section_completion() {
      // Map step titles to section names (must match backend constants)
      const section_map = {
        "Personal Details": "Personal Details",
        "Marital Information": "Marital Information",
        "Address Details": "Address Details",
        "Employment Details": "Employment Details",
        "Salary Details": "Salary Details",
        "Qualifying Criteria": "Qualifying Criteria",
        "Support Documentation": "Support Documentation",
      };

      // Check each section for saved responses
      for (let i = 0; i < this.steps.length; i++) {
        const step = this.steps[i];
        const section_name = section_map[step.title];

        if (this.isStepDisabled(i)) {
          this.steps[i].is_valid = false;
          continue;
        }

        if (section_name) {
          try {
            const responses = await this.get_saved_section_responses(
              section_name
            );
            // Mark as valid if there are saved responses with non-empty answers
            const has_valid_responses = responses.some(
              (r) => r.answer && r.answer !== ""
            );
            this.steps[i].is_valid = has_valid_responses;
          } catch (error) {
            console.error(
              `Failed to check completion for ${step.title}:`,
              error
            );
          }
        }
      }
    },
    async fetch_prepopulated_data() {
      const TOKEN = localStorage.getItem("token");
      const API_URL = `${API_BASE_URL}/verify_api/financial_information_api/get_seriti_prepopulated_data/`;
      const workflow_id = this.$store.getters.get_workflow_id;

      try {
        const response = await $.ajax({
          url: API_URL,
          method: "POST",
          contentType: "application/json",
          headers: { Authorization: `Token ${TOKEN}` },
          data: JSON.stringify({ workflow_id }),
        });

        if (response.status === "success") {
          // Store prepopulated data for use by child components
          this.prepopulated_data = response.data.seriti_data_mapping;
          this.support_documents = response.data.documents;
        }
      } catch (error) {
        console.error("Failed to fetch prepopulated data:", error);
      }
    },
    submit_application() {
      this.is_submitting = true;
      const TOKEN = localStorage.getItem("token");
      const SUBMIT_APPLICATION_URL = `${API_BASE_URL}/verify_api/financial_information_api/save_seriti_finance_application/`;

      const FORM_DATA = {
        workflow_id: this.$store.getters.get_workflow_id,
      };

      $.ajax({
        url: SUBMIT_APPLICATION_URL,
        method: "POST",
        contentType: "application/json",
        headers: { Authorization: `Token ${TOKEN}` },
        data: JSON.stringify(FORM_DATA),
        beforeSend: () => {
          Swal.fire({
            text: "Submitting Finance Application...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });
        },
        success: (response) => {
          Swal.close();

          if (response.all_sections_complete && response.status === "success") {
            this.$emit("application-submitted", response);
            this.fetch_current_service();
          } else {
            Swal.fire({
              icon: "warning",
              title: "Incomplete",
              text: response.message || "Some sections are still incomplete.",
              confirmButtonText: "OK",
            });
          }
        },
        error: (xhr) => {
          Swal.close();

          let errorMessage =
            "Failed to submit application. Please try again later.";
          if (xhr.responseJSON && xhr.responseJSON.error) {
            errorMessage = xhr.responseJSON.error;
          }

          Swal.fire({
            icon: "error",
            title: "Error",
            text: errorMessage,
            confirmButtonText: "OK",
          });
        },
      });
    },
  },
  async mounted() {
    // Restore current step from localStorage
    const savedStep = Number(localStorage.getItem("seriti_current_step"));
    if (!isNaN(savedStep) && savedStep < this.steps.length) {
      this.current_step = savedStep;
    }

    const savedEmploymentType = localStorage.getItem("seriti_employment_type");
    if (savedEmploymentType) {
      this.employment_type = savedEmploymentType;
    }

    await this.fetch_prepopulated_data();

    // Check which sections have been completed
    await this.check_section_completion();

    if (!this.employment_type) {
      try {
        const responses = await this.get_saved_section_responses(
          "Employment Details"
        );
        const employmentTypeResponse = responses.find(
          (r) => r.api_form_field === "client_employment_type"
        );
        if (employmentTypeResponse && employmentTypeResponse.answer) {
          this.employment_type = employmentTypeResponse.answer;
          localStorage.setItem("seriti_employment_type", this.employment_type);
        }
      } catch (error) {
        console.error("Failed to fetch employment type:", error);
      }
    }
  },
};
